import { useCallback, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { Answers } from './types/form';
import { questions } from './data/questions';
import { CoverScreen } from './components/CoverScreen';
import { QuestionScreen } from './components/QuestionScreen';
import { ThankYouScreen } from './components/ThankYouScreen';
const emptyAnswer = {
  optionIds: [] as string[],
  otherText: ''
};
interface AppProps {
  /** Shows the step rail at the top of each question. */
  showProgress?: boolean;
  /** Cover composition: photograph beside the welcome, or a centered lockup. */
  coverLayout?: 'split' | 'centered';
}
export function App({
  showProgress = true,
  coverLayout = 'split'
}: AppProps) {
  const [step, setStep] = useState(0);
  const [answers, setAnswers] = useState<Answers>({});
  const totalSteps = questions.length + 2;
  const questionIndex = step - 1;
  const question = questionIndex >= 0 && questionIndex < questions.length ? questions[questionIndex] : null;
  const toggleOption = useCallback((questionId: string, optionId: string, multi: boolean) => {
    setAnswers(prev => {
      const current = prev[questionId] ?? emptyAnswer;
      const alreadySelected = current.optionIds.includes(optionId);
      const optionIds = multi ? alreadySelected ? current.optionIds.filter(id => id !== optionId) : [...current.optionIds, optionId] : alreadySelected ? [] : [optionId];
      return {
        ...prev,
        [questionId]: {
          ...current,
          optionIds
        }
      };
    });
  }, []);
  const setOtherText = useCallback((questionId: string, otherText: string) => {
    setAnswers(prev => ({
      ...prev,
      [questionId]: {
        ...(prev[questionId] ?? emptyAnswer),
        otherText
      }
    }));
  }, []);
  const goNext = useCallback(() => setStep(s => Math.min(s + 1, totalSteps - 1)), [totalSteps]);
  const goBack = useCallback(() => setStep(s => Math.max(s - 1, 0)), []);
  const restart = useCallback(() => {
    setAnswers({});
    setStep(0);
  }, []);
  return <main className="min-h-full w-full bg-olive-800" data-id="element-0" data-mp="3ud1e95yxri91">
      <AnimatePresence mode="wait" initial={false} data-id="element-1" data-mp="3ucjdpx58qhvb">
        <motion.div key={step} initial={{
        opacity: 0,
        y: 14
      }} animate={{
        opacity: 1,
        y: 0
      }} exit={{
        opacity: 0,
        y: -10
      }} transition={{
        duration: 0.28,
        ease: [0.23, 1, 0.32, 1]
      }} data-id="element-2" data-mp="d57dixer88gf">
          {step === 0 && <CoverScreen layout={coverLayout} onStart={goNext} data-id="element-3" data-mp="3gja887keygi2" />}

          {question && <QuestionScreen question={question} questionNumber={questionIndex + 1} totalQuestions={questions.length} answer={answers[question.id] ?? emptyAnswer} showProgress={showProgress} isLast={questionIndex === questions.length - 1} onToggleOption={optionId => toggleOption(question.id, optionId, !!question.multi)} onOtherChange={value => setOtherText(question.id, value)} onBack={goBack} onNext={goNext} data-id="element-4" data-mp="3bk6zod585s5c" />}

          {step === totalSteps - 1 && <ThankYouScreen onRestart={restart} data-id="element-5" data-mp="yg3nv3s5e3ul" />}
        </motion.div>
      </AnimatePresence>
    </main>;
}